import { rem, Stepper } from "@mantine/core";
import {
  IconAlertTriangle,
  IconBasket,
  IconCircleCheck,
  IconTools,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { FC } from "react";
import { Order, OrderStatus } from "/@/generated/graphql";
import {
  getLastOrderStatusUpdate,
  getExpectedDeliveryDate,
} from "@jasper/shared";

const COMPLETED_STEP_COLOR = "var(--mantine-color-primaryDark-5)";

type OrderStatusStepperProps = {
  orderStatus: number;
  order: Order;
  labelsUnder?: boolean;
  grow?: boolean;
};

export const OrderStatusStepper: FC<OrderStatusStepperProps> = ({
  orderStatus,
  order,
  labelsUnder = false,
  grow = false,
}) => {
  const { producing: lastProducingDatetime, shipped: lastShippedDatetime } =
    getLastOrderStatusUpdate(order.history ?? [], order.status);

  const orderDate = new Date(
    order.history?.findLast(history => history.status === OrderStatus.Confirmed)
      ?.createdAt
  );
  const expectedDeliveryDate = getExpectedDeliveryDate(
    order.deliveryDate,
    orderDate
  );

  return (
    <Stepper
      active={orderStatus}
      wrap={false}
      w={grow ? "100%" : "70%"}
      styles={theme => ({
        stepLabel: { fontSize: theme.fontSizes.xs },
        stepDescription: { fontSize: theme.fontSizes.xs },
        separator: { marginTop: labelsUnder ? rem(25) : undefined },
        steps: { alignItems: labelsUnder ? "start" : undefined },
        step: labelsUnder
          ? { flexDirection: "column", backgroundColor: theme.white }
          : {},
        stepBody: labelsUnder
          ? { marginLeft: 0, marginTop: theme.spacing.xs, alignItems: "center" }
          : {},
        stepIcon: {
          backgroundColor: theme.white,
          borderColor: theme.white,
        },
        stepCompletedIcon: {
          backgroundColor: COMPLETED_STEP_COLOR,
          borderRadius: "50%",
        },
      })}
    >
      <Stepper.Step
        label={orderStatus === 0 ? "Commencée" : "Commandé"}
        icon={
          orderStatus === 0 ? (
            <IconAlertTriangle color="var(--mantine-color-red-6)" />
          ) : (
            <IconBasket />
          )
        }
        color={orderStatus === 0 ? "red" : "primaryDark.5"}
        completedIcon={<IconBasket />}
        description={`le ${orderDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) ?? order.createdAt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
      ></Stepper.Step>
      <Stepper.Step
        label="Fabrication"
        icon={<IconTools />}
        completedIcon={<IconTools />}
        description={lastProducingDatetime}
      ></Stepper.Step>
      <Stepper.Step
        label="Expédition"
        completedIcon={<IconTruckDelivery />}
        icon={<IconTruckDelivery />}
        description={lastShippedDatetime}
      ></Stepper.Step>
      <Stepper.Step
        icon={<IconCircleCheck />}
        completedIcon={<IconCircleCheck />}
        label={order.status === OrderStatus.Delivered ? "Livré" : "Livraison"}
        description={
          order.status === OrderStatus.Delivered &&
          order.history?.find(h => h.status === OrderStatus.Delivered)
            ? ` le ${new Date(order.history.find(h => h.status === OrderStatus.Delivered)?.createdAt || 0).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`
            : orderStatus === 0
              ? "Non planifiée"
              : ` le ${expectedDeliveryDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`
        }
      ></Stepper.Step>
    </Stepper>
  );
};
